import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import CommanTasks from './CommanTasks';
import Loader from '../../shared/components/ui/Loader';
import FormInput from '../../shared/components/ui/FormInput';
import Button from '../../shared/components/ui/Button';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { setTemplateActiveTab } from '../../../uiSlice';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { useForm } from 'react-hook-form';
import {
  getTaskTemplates,
  createTaskOtherTemplate,
  getAllTaskTemplates,
  checkDuplicateTaskTemplateName,
  getAllWorkTypes,
  createBulkTaskTemplates
} from '../services/api/implementation/impl';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import IndependentTasks from './IndependentTasks';
import GroupTasks from './GroupTasks';
import { useProjectsList } from '../../../contexts/ProjectsContext';
import { capitalizeWords } from '../utils/stringUtils';
import { resolveProjectIdFromLocation } from '../components/ai/AiUiShared';
import OpsTemplateAiChat from '../components/ai/OpsTemplateAiChat';
import OpsLiveCreatePanel from '../components/ai/OpsLiveCreatePanel';
import { clearOpsAiDraft } from '../components/ai/opsAiDraftStore';
import { Sparkles } from 'lucide-react';

const TaskTemplates = ({ embedded = false }) => {
  // Height of the sticky filter block, so the table header can stick right below it.
  const stickyBlockRef = useRef(null);
  const [stickyBlockHeight, setStickyBlockHeight] = useState(0);
  useEffect(() => {
    const el = stickyBlockRef.current;
    if (!embedded || !el) return undefined;
    const update = () => setStickyBlockHeight(el.offsetHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [embedded]);
  const location = useLocation();
  const userId = Cookies.get('userId');
  const { projects, projectsForDropdown, isProjectsLoading } = useProjectsList();
  const dispatch = useDispatch();
  const activeTab = useSelector((state) => state.ui.templateActiveTab);
  const [isloading, setisLoading] = React.useState(false);
  const { collapsed } = useSidebar();
  const [triggerBulkCreate, setTriggerBulkCreate] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [brainChatOpen, setBrainChatOpen] = React.useState(false);
  const [isApplyingAi, setIsApplyingAi] = React.useState(false);
  const [focusTemplateName, setFocusTemplateName] = React.useState(null);
  const [knownWorkTypes, setKnownWorkTypes] = React.useState([]);
  const [liveCreatePrompt, setLiveCreatePrompt] = React.useState(null);
  // Form for template creation
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm();

  const selectedProject = watch('project');
  const templateName = watch('templateName');

  const { data: TaskTemplates, refetch: refetchTaskTemplates } = useQuery({
    queryKey: ['getTaskTemplates', selectedProject, activeTab],
    queryFn: () => getTaskTemplates(selectedProject, activeTab),
    enabled: !!selectedProject && !!activeTab
  });

  const { data: AllTaskTemplates, refetch: refetchAllTaskTemplates } = useQuery({
    queryKey: ['getAllTaskTemplates', activeTab],
    queryFn: () => getAllTaskTemplates(activeTab),
    enabled: !!activeTab
  });

  const resolveOpsWorkTypeDbType = (templateType) => {
    const type = String(templateType || '').toLowerCase();
    // Independent/Group templates use Accommodation work types in this product.
    if (type === 'independent' || type === 'group') return 'Accomodation';
    return 'Common';
  };

  const filterWorkTypesForTemplate = (workTypes, templateType) => {
    const list = Array.isArray(workTypes) ? workTypes : [];
    const dbType = resolveOpsWorkTypeDbType(templateType);
    return list.filter((item) => {
      const raw = String(item?.type || '')
        .trim()
        .toLowerCase();
      if (dbType === 'Common') return raw === 'common';
      return raw === 'accomodation' || raw === 'accommodation';
    });
  };

  useEffect(() => {
    let cancelled = false;
    const loadWorkTypes = async () => {
      if (!activeTab) {
        setKnownWorkTypes([]);
        return;
      }
      try {
        const workTypes = await getAllWorkTypes();
        if (cancelled) return;
        const names = filterWorkTypesForTemplate(workTypes, activeTab)
          .map((w) => String(w?.name || '').trim())
          .filter(Boolean);
        setKnownWorkTypes(names);
      } catch {
        if (!cancelled) setKnownWorkTypes([]);
      }
    };
    loadWorkTypes();
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  useEffect(() => {
    if (location.state?.templateTab) {
      dispatch(setTemplateActiveTab(location.state.templateTab));
    }
  }, [location.state?.templateTab, dispatch]);

  // Returning from BOQ & drawings (?aiChat=1) reopens the saved AI proposal.
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    if (params.get('aiChat') === '1' || location.state?.openAiChat) {
      setBrainChatOpen(true);
    }
  }, [location.search, location.state?.openAiChat]);

  useEffect(() => {
    if (projects) {
      const projectId = resolveProjectIdFromLocation(location) || projects?.[0]?.id;
      if (projectId) {
        setValue('project', projectId);
      }
    }
  }, [projects, location, setValue]);

  // Reset template dropdown when tab or project changes and select first template
  useEffect(() => {
    if (TaskTemplates && TaskTemplates.length > 0) {
      setValue('template', TaskTemplates[0].name);
    } else {
      setValue('template', '');
    }
  }, [activeTab, selectedProject, TaskTemplates]);

  // Update active tab in redux
  const handleTabChange = (tab) => {
    dispatch(setTemplateActiveTab(tab));
  };

  // Handle template creation
  const handleCreateTemplate = async (data) => {
    if (!data.project) {
      toast.error('Please select a project');
      return;
    }
    if (!data.templateName) {
      toast.error('Please enter template name');
      return;
    }
    if (!data.template) {
      toast.error('Please select a base template');
      return;
    }

    const response = await checkDuplicateTaskTemplateName({
      project_id: data.project,
      name: data.templateName
    });

    if (response?.exist) {
      toast.error('Template name already exists');
      return;
    }

    // If user selected "Create from scratch", open bulk create modal instead of calling API
    if (data.template === 'scratch') {
      mixpanel.track('Create From Scratch Selected', {
        user_id: userId,
        project_id: data.project,
        template_name: data.templateName,
        type: activeTab,
        timestamp: new Date().toISOString()
      });
      setTriggerBulkCreate(true);
      // Reset trigger after a short delay to allow re-triggering
      setTimeout(() => setTriggerBulkCreate(false), 100);
      return;
    }

    const payload = {
      newProject_id: data.project,
      newName: capitalizeWords(data.templateName),
      type: activeTab,
      name: data.template,
      project_id: AllTaskTemplates?.find((template) => template.name === data.template)?.project?.id
    };

    setIsSubmitting(true);
    // Call API to create template
    try {
      const response = await createTaskOtherTemplate(payload);
      console.log('Template creation response:', response);
      toast.success('Template created successfully');
      setValue('templateName', '');
      // Reset to first template after creation
      if (TaskTemplates && TaskTemplates.length > 0) {
        setValue('template', TaskTemplates[0].id);
      }
      await refetchTaskTemplates();
      setIsSubmitting(false);
    } catch (error) {
      setIsSubmitting(false);
      console.error('Error creating template:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Error creating template');
    }
  };

  const TransformedTaskTemplates = AllTaskTemplates?.map((template, index) => ({
    label: template.name + ' (' + template?.project?.name + ')',
    value: template.name,
    id: template.id,
    selected: index === 0
  }));

  const subTabs = (
    <div className="flex items-center gap-6 border-b border-transparent">
      {[
        { key: 'common', label: 'Common Tasks' },
        { key: 'independent', label: 'Independent Tasks' },
        { key: 'group', label: 'Group Tasks' }
      ].map((tab) => {
        const isActive = activeTab === tab.key;
        return (
          <button
            key={tab.key}
            type="button"
            className={`relative pb-2.5 text-sm font-semibold transition-colors duration-200 ${
              isActive ? 'text-[#2A565E]' : 'text-gray-400 hover:text-gray-600'
            }`}
            onClick={() => handleTabChange(tab.key)}
          >
            {tab.label}
            {isActive ? (
              <span className="absolute bottom-0 left-0 right-0 h-[2px] rounded-full bg-[#2A565E]" />
            ) : null}
          </button>
        );
      })}
    </div>
  );

  const templateForm = (
    <div className="w-full">
      <style>{`
        .task-template-create-row .form-field-shell > div {
          width: 100% !important;
        }
        .task-template-create-row .form-field-shell .relative > div {
          min-width: 0;
        }
        .task-template-create-row .form-field-shell .relative > div > span:first-child {
          display: block;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      `}</style>
      <form
        onSubmit={handleSubmit(handleCreateTemplate)}
        className="task-template-create-row flex w-full flex-wrap items-end gap-3"
      >
        <div className="form-field-shell w-[200px] min-w-[160px] shrink-0">
          <FormInput
            width={100}
            label="Project"
            register={register}
            register_key="project"
            isRequired={true}
            type="dropdown"
            options={[
              { label: 'Select Project', value: '', disabled: true, selected: true },
              ...projectsForDropdown
            ]}
            validation={{ required: 'Project is required' }}
            errors={errors}
            hideAstrik={true}
          />
        </div>
        <div className="form-field-shell w-[200px] min-w-[160px] shrink-0">
          <FormInput
            width={100}
            label="Template name"
            register={register}
            register_key="templateName"
            isRequired={true}
            type="text"
            placeholder="e.g. G+12 Foundation"
            validation={{ required: 'Template name is required' }}
            errors={errors}
            hideAstrik={true}
          />
        </div>
        <div className="form-field-shell min-w-[220px] max-w-[300px] flex-1">
          <FormInput
            width={100}
            label="Copy from"
            register={register}
            register_key="template"
            isRequired={true}
            type="dropdown"
            options={[
              ...(TransformedTaskTemplates || []),
              { label: 'Create from scratch', value: 'scratch' }
            ]}
            validation={{ required: 'Choose a template to copy, or Create from scratch' }}
            errors={errors}
            hideAstrik={true}
          />
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-2">
          <Button
            type="submit"
            customStyle={{ whiteSpace: 'nowrap', minHeight: '40px' }}
            disabled={isSubmitting || isProjectsLoading}
          >
            {isSubmitting ? 'Creating…' : 'Create Template'}
          </Button>
          <button
            type="button"
            onClick={() => {
              mixpanel.track('Ops Create with AI Opened', {
                user_id: userId,
                project_id: selectedProject,
                template_type: activeTab,
                timestamp: new Date().toISOString()
              });
              setBrainChatOpen(true);
            }}
            disabled={!selectedProject}
            className="inline-flex h-10 items-center gap-1.5 whitespace-nowrap rounded-lg border border-indigo-200 bg-indigo-50 px-4 text-sm font-semibold text-indigo-800 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-40"
            title="Create task templates with AI — not live site tasks"
          >
            <Sparkles className="h-4 w-4 shrink-0" />
            Create with AI
          </button>
        </div>
      </form>
      <p className="mt-1.5 text-[11px] leading-snug text-gray-500">
        <span className="font-medium text-gray-600">Copy from</span> chooses what to clone when you
        Create Template. Tabs below are for viewing only.
      </p>
    </div>
  );

  const handleApplyAiTemplates = async (applyPayload) => {
    const projectId = applyPayload?.project_id || selectedProject;
    if (!projectId) {
      toast.error('Select a project before applying AI templates');
      return;
    }
    const templates = applyPayload?.templates;
    if (!Array.isArray(templates) || !templates.length) {
      toast.error('No proposed tasks to apply');
      return;
    }

    const templateType = String(applyPayload.templateType || activeTab || 'common').toLowerCase();
    if (!['common', 'independent', 'group'].includes(templateType)) {
      toast.error('Choose a valid template type before applying');
      return;
    }

    const appliedTemplateName = String(applyPayload.template_name || templateName || '').trim();
    if (!appliedTemplateName || appliedTemplateName.toLowerCase() === 'ai proposed template') {
      toast.error('Enter a specific template name before applying');
      return;
    }

    const normalizedTaskNames = templates.map((t) => String(t?.name || '').trim());
    if (normalizedTaskNames.some((name) => !name)) {
      toast.error('Every proposed task needs a name');
      return;
    }
    const taskNameKeys = normalizedTaskNames.map((name) => name.toLowerCase());
    if (new Set(taskNameKeys).size !== taskNameKeys.length) {
      toast.error('Task names must be unique within the template');
      return;
    }
    const isAccommodationType = templateType === 'independent' || templateType === 'group';
    const isFloorwise = (task) => isAccommodationType && Boolean(task?.floorwise);
    // A floor-wise task may depend on itself — that is how floors chain.
    const invalidDependency = templates.find((task, index) =>
      (Array.isArray(task?.dependency) ? task.dependency : []).some((dependency) => {
        const dependencyKey = String(dependency || '')
          .trim()
          .toLowerCase();
        return (
          !dependencyKey ||
          (dependencyKey === taskNameKeys[index] && !isFloorwise(task)) ||
          !taskNameKeys.includes(dependencyKey)
        );
      })
    );
    if (invalidDependency) {
      toast.error(
        `Check dependencies for “${String(invalidDependency.name || '').trim()}” — each must reference another proposed task`
      );
      return;
    }

    const missingOrder = templates.find((task) => isFloorwise(task) && !task?.working_order);
    if (missingOrder) {
      toast.error(
        `Choose Bottom Up or Top Down for floor-wise task “${String(missingOrder.name || '').trim()}”`
      );
      return;
    }

    const workTypeLabel = isAccommodationType ? 'Accommodation' : 'Common';

    setIsApplyingAi(true);
    try {
      const duplicate = await checkDuplicateTaskTemplateName({
        project_id: Number(projectId),
        name: appliedTemplateName
      });
      if (duplicate?.exist) {
        toast.error(`A template named “${appliedTemplateName}” already exists in this project`);
        return;
      }

      const workTypes = await getAllWorkTypes();
      const list = filterWorkTypesForTemplate(workTypes, templateType);
      const defaultWorkTypeId = list.length === 1 ? list[0]?.id : null;
      if (!list.length) {
        toast.error(`No ${workTypeLabel} work types found. Create a work type first.`);
        return;
      }

      const resolveWorkTypeId = (workTypeName) => {
        const key = String(workTypeName || '')
          .trim()
          .toLowerCase();
        if (!key) return defaultWorkTypeId;
        const match = list.find(
          (w) =>
            String(w.name || '')
              .trim()
              .toLowerCase() === key
        );
        return match?.id || null;
      };

      const missingWorkTypeCount = templates.filter(
        (task) => !resolveWorkTypeId(task?.workTypeName)
      ).length;
      if (missingWorkTypeCount) {
        toast.error(
          `Choose a ${workTypeLabel} work type for all ${missingWorkTypeCount} task${missingWorkTypeCount > 1 ? 's' : ''} before applying`
        );
        return;
      }

      const unresolvedWorkTypes = [
        ...new Set(
          templates
            .map((task) => String(task?.workTypeName || '').trim())
            .filter((name) => name && !resolveWorkTypeId(name))
        )
      ];
      if (unresolvedWorkTypes.length) {
        toast.error(
          `Unknown ${workTypeLabel} work type${unresolvedWorkTypes.length > 1 ? 's' : ''}: ${unresolvedWorkTypes.join(', ')}`
        );
        return;
      }

      const accomodationType =
        templateType === 'independent' ? 'Independent' : templateType === 'group' ? 'Group' : null;

      const finalTaskNames = normalizedTaskNames.map((name) => capitalizeWords(name));
      const finalTaskNameByKey = new Map(
        taskNameKeys.map((key, index) => [key, finalTaskNames[index]])
      );
      const bulkTemplates = templates.map((t, index) => ({
        name: finalTaskNames[index],
        delay: Number(t.delay) || 0,
        duration: Math.max(1, Number(t.duration) || 1),
        dependency: Array.isArray(t.dependency)
          ? t.dependency.map((name) =>
              finalTaskNameByKey.get(
                String(name || '')
                  .trim()
                  .toLowerCase()
              )
            )
          : [],
        drawing_required: Boolean(t.drawing_required),
        subActivity: Array.isArray(t.subActivity) ? t.subActivity : [],
        workType_id: resolveWorkTypeId(t.workTypeName),
        // The set's type wins: tasks keep a stale per-row value if the type
        // was switched in the preview.
        accomodationType,
        floorwise: isFloorwise(t),
        expandable: isFloorwise(t),
        working_order: isFloorwise(t) ? t.working_order : null,
        unitwise: templateType === 'group' && Boolean(t.unitwise),
        project_id: Number(projectId)
      }));

      await createBulkTaskTemplates({
        project_id: Number(projectId),
        template_name: appliedTemplateName,
        templates: bulkTemplates,
        // Lets the backend diff what was saved against what was proposed.
        // Optional: a manual bulk create simply omits it.
        ai_request_id: applyPayload?.requestId || undefined
      });

      if (templateType === 'common' || templateType === 'independent' || templateType === 'group') {
        dispatch(setTemplateActiveTab(templateType));
      }
      setFocusTemplateName(appliedTemplateName);
      await refetchTaskTemplates();
      setBrainChatOpen(false);
      clearOpsAiDraft();

      const drawingTaskCount = bulkTemplates.filter((t) => t.drawing_required).length;
      const hasDrawings = drawingTaskCount > 0;
      const uniqueWorkTypeIds = [
        ...new Set(bulkTemplates.map((t) => Number(t.workType_id)).filter(Boolean))
      ];

      toast.success(
        `Applied ${bulkTemplates.length} AI task(s) — opened “${appliedTemplateName}”`
      );
      setLiveCreatePrompt({
        projectId: Number(projectId),
        templateName: appliedTemplateName,
        templateType,
        workTypeIds: uniqueWorkTypeIds,
        hasDrawings,
        drawingTaskCount
      });
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to apply AI templates');
    } finally {
      setIsApplyingAi(false);
    }
  };

  const brainChat = (
    <OpsTemplateAiChat
      open={brainChatOpen}
      onClose={() => setBrainChatOpen(false)}
      projectId={selectedProject || null}
      contextHints={{
        // Only where the chat was opened from — the AI still asks whether the
        // work is site-wide or tied to an accommodation.
        activeTab,
        templateNameDraft: templateName || undefined,
        knownWorkTypes
      }}
      onApply={handleApplyAiTemplates}
      isApplying={isApplyingAi}
    />
  );

  const liveCreatePanel = (
    <OpsLiveCreatePanel
      open={Boolean(liveCreatePrompt)}
      onClose={() => setLiveCreatePrompt(null)}
      projectId={liveCreatePrompt?.projectId}
      templateName={liveCreatePrompt?.templateName}
      templateType={liveCreatePrompt?.templateType}
      workTypeIds={liveCreatePrompt?.workTypeIds}
      hasDrawings={liveCreatePrompt?.hasDrawings}
      drawingTaskCount={liveCreatePrompt?.drawingTaskCount}
    />
  );

  const taskGrid = (
    <>
      {activeTab === 'common' ? (
        <CommanTasks
          setisLoading={setisLoading}
          TaskTemplates={TaskTemplates || []}
          projectId={selectedProject}
          triggerBulkCreate={triggerBulkCreate}
          bulkCreateTemplateName={templateName}
          focusTemplateName={focusTemplateName}
          onFocusedTemplate={() => setFocusTemplateName(null)}
          refreshtemplates={() => {
            refetchTaskTemplates();
            setValue('template', TaskTemplates[0].name);
            setValue('templateName', '');
          }}
        />
      ) : activeTab === 'independent' ? (
        <IndependentTasks
          setisLoading={setisLoading}
          TaskTemplates={TaskTemplates || []}
          projectId={selectedProject}
          triggerBulkCreate={triggerBulkCreate}
          bulkCreateTemplateName={templateName}
          focusTemplateName={focusTemplateName}
          onFocusedTemplate={() => setFocusTemplateName(null)}
          refreshtemplates={() => {
            refetchTaskTemplates();
            setValue('template', TaskTemplates[0].name);
            setValue('templateName', '');
          }}
        />
      ) : activeTab === 'group' ? (
        <GroupTasks
          setisLoading={setisLoading}
          TaskTemplates={TaskTemplates || []}
          projectId={selectedProject}
          triggerBulkCreate={triggerBulkCreate}
          bulkCreateTemplateName={templateName}
          focusTemplateName={focusTemplateName}
          onFocusedTemplate={() => setFocusTemplateName(null)}
          refreshtemplates={() => {
            refetchTaskTemplates();
            setValue('template', TaskTemplates[0].name);
            setValue('templateName', '');
          }}
        />
      ) : null}
    </>
  );

  if (embedded) {
    return (
      <div
        className="w-full embedded-task-templates"
        style={{ '--tt-sticky-h': `${stickyBlockHeight}px` }}>
        <style>{`
          .embedded-task-templates .tt-table-wrap { overflow: visible !important; }
          .embedded-task-templates .tt-template-tabs {
            position: sticky;
            top: calc(4.1rem + var(--tt-sticky-h, 0px));
            z-index: 16;
            height: 3.25rem;
            margin: 0 -1.5rem !important;
            padding: 0 1.5rem;
            display: flex;
            align-items: center;
            background-color: #fff;
          }
          .embedded-task-templates .tt-table-wrap thead th {
            position: sticky;
            top: calc(4.1rem + var(--tt-sticky-h, 0px) + 3.25rem);
            z-index: 15;
            background-color: #e5e7eb;
          }
          .embedded-task-templates .mt-\\[14rem\\] { margin-top: 0 !important; }
          .embedded-task-templates .mt-\\[13\\.5rem\\] { margin-top: 0 !important; }
          .embedded-task-templates .mt-\\[12rem\\] { margin-top: 0 !important; }
          .embedded-task-templates .mt-\\[8rem\\] { margin-top: 0 !important; }
        `}</style>
        <Loader isVisible={isloading || isProjectsLoading} />
        <div
          ref={stickyBlockRef}
          className="sticky top-[4.1rem] z-20 bg-white border-b border-gray-200">
          <div className="px-6 pt-3">{subTabs}</div>
          <div className="px-6 pb-3 pt-3">{templateForm}</div>
        </div>
        <div className="px-6">{taskGrid}</div>
        {brainChat}
        {liveCreatePanel}
      </div>
    );
  }

  return (
    <Layout>
      <div
        className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
          collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
        }`}
      >
        <Header backArrow={false} data={{ name: 'TaskTemplates' }} />
      </div>

      <Loader isVisible={isloading || isProjectsLoading} />

      <div
        className={`fixed top-[4.1rem] z-30 bg-white px-6 pt-3 transition-all duration-300 ease-in-out ${
          collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
        }`}
      >
        {subTabs}
      </div>

      <div
        className={`fixed top-[7.1rem] z-20 border-t border-gray-200 bg-white px-6 py-3 transition-all duration-300 ease-in-out ${
          collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
        }`}
      >
        {templateForm}
      </div>

      <div
        style={{
          marginTop: activeTab === 'independent' || activeTab === 'group' ? '12rem' : '8rem'
        }}
      >
        <GridWrapper width={100}>{taskGrid}</GridWrapper>
      </div>
      {brainChat}
      {liveCreatePanel}
    </Layout>
  );
};

export default TaskTemplates;
